import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
} from '@ea-lab/reactive-json';
import {
    saveDocDirectory,
    saveDocPage,
    getDirectoryAccess,
    saveDirectoryAccess,
    getDirectoryAccessSubjects,
    getDirectoryUserPermissions,
    moveDocItem,
    moveDocItemPreview,
} from '../../../services/api';
import DocAccessPanel from './DocAccessPanel';
import { DocFiltersContext } from '../DocFiltersContext';
import {
    AlertTriangle,
    ArrowRight,
    Ban,
    Check,
    ChevronRight,
    Eye,
    FileEdit,
    FolderEdit,
    FolderInput,
    FolderPlus,
    Info,
    Loader2,
    Pencil,
    Plus,
    Shield,
    ShieldAlert,
    ShieldCheck,
    ShieldOff,
    Trash2,
    Users,
    X,
} from 'lucide-react';
import { PictoPicker } from '../../shared/fields/PictoPicker';

const MAX_NAME = 120;

const DIR_TABS = [
    { id: 'rename', label: 'Renommer', icon: FolderEdit },
    { id: 'move', label: 'Déplacer', icon: FolderInput },
    { id: 'access', label: 'Accès', icon: ShieldCheck },
];

const PAGE_TABS = [
    { id: 'rename', label: 'Renommer', icon: FileEdit },
    { id: 'move', label: 'Déplacer', icon: FolderInput },
    { id: 'access', label: 'Accès', icon: ShieldCheck },
];

const PERM_KEYS = ['canView', 'canUpdate', 'canDelete', 'canCreateChildren'];
const PERM_LABELS = { canView: 'Voir', canUpdate: 'Modifier', canDelete: 'Supprimer', canCreateChildren: 'Créer' };

function PermIcon({ granted }) {
    return granted
        ? <Check size={13} className="text-emerald-600" />
        : <X size={13} className="text-stone-300" />;
}

function GrantLabel({ grant }) {
    if (grant.grantType === 'user') {
        return <span className="text-sm font-medium text-stone-800">{grant.targetName}</span>;
    }
    if (grant.grantType === 'wiki_role') {
        return (
            <span className="text-sm font-medium text-stone-800">
                <span className="text-teal-600 text-3xs mr-1">Wiki</span>
                {grant.roleName || grant.roleId}
                {grant.wikiGroupName && (
                    <span className="text-stone-400 text-xs ml-1">({grant.wikiGroupName})</span>
                )}
            </span>
        );
    }
    return (
        <span className="text-sm font-medium text-stone-800">
            <span className="text-iris-600 text-3xs mr-1">Rôle</span>
            {grant.roleName || grant.roleId}
        </span>
    );
}

/* ── Move preview inline (access diff) ── */

const MOVE_PERM_DEFS = [
    { key: 'canView', label: 'Lecture', short: 'L', icon: Eye },
    { key: 'canUpdate', label: 'Écriture', short: 'É', icon: Pencil },
    { key: 'canDelete', label: 'Suppression', short: 'S', icon: Trash2 },
    { key: 'canCreateChildren', label: 'Création', short: 'C', icon: FolderPlus },
];

function MovePermBadges({ grant }) {
    return (
        <div className="flex items-center gap-1">
            {MOVE_PERM_DEFS.map(({ key, label, short, icon: Icon }) => (
                grant[key] ? (
                    <span key={key} title={label} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 text-[10px] font-medium leading-none">
                        <Icon size={10} className="opacity-60" />{short}
                    </span>
                ) : null
            ))}
        </div>
    );
}

function MoveDiffSection({ icon, title, count, color, children }) {
    const colors = {
        red: { heading: 'text-red-600', border: 'border-red-100', badge: 'bg-red-100 text-red-600' },
        green: { heading: 'text-green-600', border: 'border-green-100', badge: 'bg-green-100 text-green-600' },
        amber: { heading: 'text-amber-600', border: 'border-amber-100', badge: 'bg-amber-100 text-amber-600' },
    };
    const c = colors[color] || colors.amber;
    return (
        <div>
            <div className={`flex items-center gap-1.5 mb-1.5 ${c.heading}`}>
                {icon}
                <span className="text-[11px] font-semibold uppercase tracking-wider">{title}</span>
                <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${c.badge}`}>{count}</span>
            </div>
            <div className={`border ${c.border} rounded-lg divide-y divide-stone-100 overflow-hidden`}>
                {children}
            </div>
        </div>
    );
}

function MoveGrantRow({ grant, variant }) {
    const bg = variant === 'lost' ? 'bg-red-50/60' : 'bg-green-50/60';
    const name = grant.targetName || `#${grant.targetId}`;
    return (
        <div className={`flex items-center justify-between gap-3 px-3 py-2 ${bg}`}>
            <div className="text-xs font-medium text-stone-800 truncate">{name}</div>
            <MovePermBadges grant={grant} />
        </div>
    );
}

function MovePreviewInline({ preview, isDir, preserveAccess, onTogglePreserve }) {
    const diff = preview?.diff;
    const hasChanges = diff?.hasChanges;

    if (!diff) return null;

    if (!hasChanges) {
        return (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                <ShieldCheck size={16} className="flex-shrink-0" />
                <span>Les droits d'accès restent identiques après déplacement.</span>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 text-xs leading-relaxed">
                <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
                <span>Le nouvel emplacement a des droits d'accès différents :</span>
            </div>
            {diff.lost?.length > 0 && (
                <MoveDiffSection icon={<ShieldOff size={11} />} title="N'auront plus accès" count={diff.lost.length} color="red">
                    {diff.lost.map((g, i) => <MoveGrantRow key={i} grant={g} variant="lost" />)}
                </MoveDiffSection>
            )}
            {diff.gained?.length > 0 && (
                <MoveDiffSection icon={<ShieldCheck size={11} />} title="Auront accès" count={diff.gained.length} color="green">
                    {diff.gained.map((g, i) => <MoveGrantRow key={i} grant={g} variant="gained" />)}
                </MoveDiffSection>
            )}
            {diff.changed?.length > 0 && (
                <MoveDiffSection icon={<Shield size={11} />} title="Permissions modifiées" count={diff.changed.length} color="amber">
                    {diff.changed.map((c, i) => {
                        const name = c.subject?.targetName || `#${c.subject?.targetId}`;
                        return (
                            <div key={i} className="px-3 py-2 bg-amber-50/60">
                                <div className="text-xs font-medium text-stone-800 truncate mb-1.5">{name}</div>
                                <div className="flex items-center gap-2">
                                    <MovePermBadges grant={c.before} />
                                    <ArrowRight size={10} className="text-stone-400 flex-shrink-0" />
                                    <MovePermBadges grant={c.after} />
                                </div>
                            </div>
                        );
                    })}
                </MoveDiffSection>
            )}
            {isDir && (
                <div className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={preserveAccess}
                            onChange={(e) => onTogglePreserve(e.target.checked)}
                            className="mt-0.5 accent-iris-600"
                        />
                        <div>
                            <p className="text-xs font-medium text-stone-700">Conserver les droits actuels</p>
                            <p className="text-[11px] text-stone-500 mt-0.5 leading-relaxed">
                                L'héritage sera coupé et les droits effectifs actuels seront copiés en droits locaux.
                            </p>
                        </div>
                    </label>
                </div>
            )}
        </div>
    );
}

/* ── Promotion form (when destination = Root) ── */

function PromotionForm({ preview, projectId, onProjectChange, projects, categoryIds, onCategoryToggle, spaceCategories }) {
    const groupedCategories = useMemo(() => {
        if (!Array.isArray(spaceCategories) || spaceCategories.length === 0) return [];
        const roots = spaceCategories.filter((c) => !c.parentId || c.parentId === 0);
        return roots.map((root) => ({
            root,
            children: spaceCategories.filter((c) => c.parentId === root.id),
        }));
    }, [spaceCategories]);

    const ownerName = preview?.sourceSpaceOwnerName || '';

    return (
        <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-iris-50 border border-iris-200 text-iris-800 text-xs leading-relaxed">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-iris-500" />
                <div>
                    <p className="font-medium">Ce répertoire va devenir un espace indépendant</p>
                    <p className="mt-0.5 text-iris-600">
                        Il sera détaché de l'espace « {preview?.sourceSpaceName || '…'} » et visible au niveau racine.
                    </p>
                </div>
            </div>

            {ownerName && (
                <div>
                    <label className="text-xs font-medium text-stone-600 mb-1 block">Propriétaire (hérité)</label>
                    <div className="px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-700">{ownerName}</div>
                </div>
            )}

            <div>
                <label className="text-xs font-medium text-stone-600 mb-1 block">Projet lié</label>
                <select
                    value={projectId}
                    onChange={(e) => onProjectChange(e.target.value)}
                    className="select"
                >
                    <option value="">Aucun projet</option>
                    {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>

            {groupedCategories.length > 0 && (
                <div>
                    <label className="text-xs font-medium text-stone-600 mb-1.5 block">Catégories</label>
                    <div className="p-3 border border-stone-200 rounded-lg bg-white space-y-2">
                        {groupedCategories.map(({ root, children }) => (
                            <div key={root.id}>
                                <button
                                    type="button"
                                    onClick={() => onCategoryToggle(root.id)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-all ${
                                        categoryIds.includes(root.id)
                                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium'
                                            : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300 hover:bg-stone-100'
                                    }`}
                                >
                                    {categoryIds.includes(root.id) && <Check size={10} />}
                                    {root.name}
                                </button>
                                {children.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-1 ml-4 pl-3 border-l-2 border-stone-100">
                                        {children.map((child) => (
                                            <button
                                                key={child.id}
                                                type="button"
                                                onClick={() => onCategoryToggle(child.id)}
                                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-all ${
                                                    categoryIds.includes(child.id)
                                                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium'
                                                        : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300 hover:bg-stone-100'
                                                }`}
                                            >
                                                {categoryIds.includes(child.id) && <Check size={10} />}
                                                {child.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ════════════════════════════════════════════════════════════════
   DirectorySettingsBody — Pure React UI (no ReactiveJSON dependency)
   Handles both directories and pages (itemType prop).
   ════════════════════════════════════════════════════════════════ */

export function DirectorySettingsBody({ itemId, itemType = 'directory', currentName, currentIcon = '', currentParentId, initialTab = 'rename', onClose, onSaved }) {
    const isDir = itemType === 'directory';
    const allTabs = isDir ? DIR_TABS : PAGE_TABS;
    const filtersCtx = useContext(DocFiltersContext);

    const [tab, setTab] = useState(initialTab);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [newName, setNewName] = useState('');
    const [newIcon, setNewIcon] = useState('');

    const [allDirs, setAllDirs] = useState([]);
    const [targetParentId, setTargetParentId] = useState('');
    const [loadingDirs, setLoadingDirs] = useState(false);

    // Move tab: permissions, preview, promotion
    const [canMove, setCanMove] = useState(null);
    const [movePreviewData, setMovePreviewData] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [preserveAccess, setPreserveAccess] = useState(true);
    const [promoteProjectId, setPromoteProjectId] = useState('');
    const [promoteCategoryIds, setPromoteCategoryIds] = useState([]);
    const [promoteProjects, setPromoteProjects] = useState([]);

    const [accessInfo, setAccessInfo] = useState(null);
    const [localGrants, setLocalGrants] = useState([]);
    const [overrideAccess, setOverrideAccess] = useState(false);
    const [loadingAccess, setLoadingAccess] = useState(false);
    const [subjects, setSubjects] = useState(null);
    const [subjectsLoading, setSubjectsLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addType, setAddType] = useState('user');
    const [addTargetId, setAddTargetId] = useState('');
    const [addRoleId, setAddRoleId] = useState('');
    const [addWikiGroupId, setAddWikiGroupId] = useState('');
    const [addPerms, setAddPerms] = useState({ canView: true, canUpdate: false, canDelete: false, canCreateChildren: false });

    const visibleTabs = useMemo(() => {
        if (canMove === false) return allTabs.filter((t) => t.id !== 'move');
        return allTabs;
    }, [allTabs, canMove]);

    // Fallback to 'rename' if permission check removes the currently active tab.
    useEffect(() => {
        if (canMove === false && tab === 'move') {
            setTab('rename');
        }
    }, [canMove, tab]);

    useEffect(() => {
        setNewName(currentName || '');
        setNewIcon(currentIcon || '');
        setTargetParentId(String(currentParentId || ''));
        setError('');
        setSuccess('');
        setTab(initialTab);
        setShowAddForm(false);
        setSubjects(null);
        setAccessInfo(null);
        setAllDirs([]);
        setMovePreviewData(null);
        setCanMove(null);
    }, [itemId, currentName, currentIcon, currentParentId, initialTab]);

    // Fetch user permissions to decide if move tab is visible.
    useEffect(() => {
        if (!itemId) return;
        const dirId = isDir ? itemId : currentParentId;
        if (!dirId) { setCanMove(false); return; }
        getDirectoryUserPermissions(dirId)
            .then((data) => setCanMove(data.isAdmin || data.isOwner))
            .catch(() => setCanMove(false));
    }, [itemId, currentParentId, isDir]);

    useEffect(() => {
        if (allDirs.length > 0) return;
        setLoadingDirs(true);
        fetch('/decoupled-api/taxonomy_term/doc_directory/collection?display=decoupled', { credentials: 'include' })
            .then((r) => r.json())
            .then((data) => {
                const dirs = (data || []).filter(Boolean).map((item) => {
                    const d = item.data ?? item;
                    const rawP = d.parentId;
                    const pid = Array.isArray(rawP) ? parseInt(rawP[0] || '0', 10) : parseInt(rawP || '0', 10);
                    return { id: d.id, name: d.name, parentId: pid || 0 };
                });
                setAllDirs(isDir ? dirs.filter((d) => d.id !== parseInt(itemId, 10)) : dirs);
            })
            .catch(() => setAllDirs([]))
            .finally(() => setLoadingDirs(false));
    }, [itemId, isDir, allDirs.length]);

    // Auto-fetch move preview when a different destination is selected.
    useEffect(() => {
        if (tab !== 'move') return;
        const pid = targetParentId !== '' ? parseInt(targetParentId, 10) : NaN;
        if (isNaN(pid) || pid === (currentParentId || 0)) {
            setMovePreviewData(null);
            return;
        }
        let cancelled = false;
        setLoadingPreview(true);
        setMovePreviewData(null);
        moveDocItemPreview({ itemId: parseInt(itemId, 10), itemType, targetParentId: pid })
            .then((data) => { if (!cancelled) { setMovePreviewData(data); setPreserveAccess(true); } })
            .catch((err) => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoadingPreview(false); });
        return () => { cancelled = true; };
    }, [tab, targetParentId, itemId, itemType, currentParentId]);

    // Fetch projects when promotion is selected.
    useEffect(() => {
        if (targetParentId !== '0' || promoteProjects.length > 0) return;
        fetch('/decoupled-api/group/project/collection?display=decoupled', { credentials: 'include' })
            .then((r) => r.json())
            .then((data) => {
                const items = (data || []).filter(Boolean).map((item) => {
                    const d = item.data ?? item;
                    return { id: d.id, name: d.name || `Projet #${d.id}` };
                });
                setPromoteProjects(items);
            })
            .catch(() => setPromoteProjects([]));
    }, [targetParentId, promoteProjects.length]);

    useEffect(() => {
        if (tab !== 'access' || accessInfo || !isDir || !itemId) return;
        setLoadingAccess(true);
        getDirectoryAccess(itemId)
            .then((data) => {
                setAccessInfo(data);
                setLocalGrants(data.localGrants || []);
                setOverrideAccess(data.overrideAccess || false);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoadingAccess(false));
    }, [tab, itemId, accessInfo, isDir]);

    const loadSubjects = useCallback(async () => {
        if (subjects || subjectsLoading || !itemId) return;
        setSubjectsLoading(true);
        try {
            const data = await getDirectoryAccessSubjects(itemId);
            setSubjects(data);
        } catch {
            setSubjects({ users: [], projectMembers: [], availableRoles: [] });
        } finally {
            setSubjectsLoading(false);
        }
    }, [itemId, subjects, subjectsLoading]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const nameChanged = newName.trim() && newName.trim() !== currentName;
    // L'icône ne concerne plus que les PAGES : le vocabulaire des répertoires
    // n'en porte plus (`field_icon` supprimé).
    const iconChanged = !isDir && newIcon !== (currentIcon || '');
    const hasRenameChanges = nameChanged || iconChanged;

    const handleRename = useCallback(async () => {
        const trimmed = newName.trim();
        if (!trimmed) { setError('Le nom est obligatoire.'); return; }
        if (trimmed.length > MAX_NAME) { setError(`${MAX_NAME} caractères max.`); return; }
        if (!hasRenameChanges) { setError('Aucune modification.'); return; }
        setSaving(true); setError('');
        try {
            if (isDir) {
                const payload = { id: itemId, name: trimmed };
                await saveDocDirectory(payload);
            } else {
                const payload = { id: itemId, title: trimmed };
                if (iconChanged) payload.icon = newIcon;
                await saveDocPage(payload);
            }
            setSuccess('Mis à jour.');
            onSaved?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, [itemId, newName, newIcon, currentName, currentIcon, isDir, onSaved, hasRenameChanges, iconChanged]);

    const handleMove = useCallback(async () => {
        const pid = targetParentId !== '' ? parseInt(targetParentId, 10) : NaN;
        if (isNaN(pid)) return;
        if (pid === (currentParentId || 0)) { setError("L'emplacement est identique."); return; }
        if (pid < 0) return;
        if (pid === 0 && !isDir) { setError('Sélectionnez un répertoire de destination.'); return; }
        setSaving(true); setError('');
        try {
            const params = {
                itemId: parseInt(itemId, 10),
                itemType,
                targetParentId: pid,
                weight: 0,
                preserveAccess,
            };
            if (pid === 0) {
                params.spaceConfig = {};
                if (promoteProjectId) params.spaceConfig.projectId = parseInt(promoteProjectId, 10);
                if (promoteCategoryIds.length > 0) params.spaceConfig.categoryIds = promoteCategoryIds;
            }
            await moveDocItem(params);
            setSuccess('Déplacé avec succès.');
            onSaved?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, [itemId, itemType, targetParentId, currentParentId, isDir, preserveAccess, promoteProjectId, promoteCategoryIds, onSaved]);

    const handleSaveAccess = useCallback(async () => {
        setSaving(true); setError('');
        try {
            const payload = {
                overrideAccess,
                accessGrants: localGrants.map((g) => ({
                    grantType: g.grantType,
                    targetId: g.targetId,
                    roleId: g.roleId || '',
                    canView: g.canView,
                    canUpdate: g.canUpdate,
                    canDelete: g.canDelete,
                    canCreateChildren: g.canCreateChildren,
                })),
            };
            const updated = await saveDirectoryAccess(itemId, payload);
            setAccessInfo(updated);
            setLocalGrants(updated.localGrants || []);
            setOverrideAccess(updated.overrideAccess || false);
            setSuccess('Accès enregistrés.');
            setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, [itemId, localGrants, overrideAccess]);

    const handleAddGrant = useCallback(() => {
        const tid = parseInt(addTargetId, 10);
        if (!tid && addType === 'user') return;
        if (addType === 'project_role' && !addRoleId) return;
        if (addType === 'wiki_role' && (!addWikiGroupId || !addRoleId)) return;

        const g = {
            grantType: addType,
            targetId: addType === 'wiki_role' ? parseInt(addWikiGroupId, 10) : (tid || (subjects?.projectId || 0)),
            targetName: '',
            roleId: (addType === 'project_role' || addType === 'wiki_role') ? addRoleId : '',
            roleName: '',
            projectName: '',
            wikiGroupName: '',
            ...addPerms,
            isInherited: false,
        };

        if (addType === 'user' && subjects?.users) {
            const u = subjects.users.find((u) => String(u.id) === String(g.targetId));
            if (u) g.targetName = u.name;
        }
        if (addType === 'project_role') {
            g.targetId = subjects?.projectId || accessInfo?.projectId || 0;
            g.targetName = accessInfo?.projectName || '';
            g.projectName = accessInfo?.projectName || '';
            const r = (subjects?.availableRoles || []).find((r) => String(r.id) === String(addRoleId));
            if (r) g.roleName = r.label;
        }
        if (addType === 'wiki_role') {
            const wg = (subjects?.wikiGroups || []).find((wg) => String(wg.id) === String(addWikiGroupId));
            g.wikiGroupName = wg?.name || '';
            g.targetName = wg?.name || '';
            const wr = (subjects?.availableWikiRoles || []).find((r) => String(r.id) === String(addRoleId));
            if (wr) g.roleName = wr.label;
        }

        setLocalGrants((prev) => [...prev, g]);
        setAddTargetId(''); setAddRoleId(''); setAddWikiGroupId('');
        setAddPerms({ canView: true, canUpdate: false, canDelete: false, canCreateChildren: false });
        setShowAddForm(false);
    }, [addType, addTargetId, addRoleId, addWikiGroupId, addPerms, subjects, accessInfo]);

    const hasProject = useMemo(() => !!accessInfo?.projectId, [accessInfo]);
    const isRoot = useMemo(() => accessInfo?.isRoot ?? false, [accessInfo]);
    const effectiveGrants = useMemo(() => accessInfo?.effectiveGrants || [], [accessInfo]);

    const dirOptions = useMemo(() => {
        const rootDirs = allDirs.filter((d) => d.parentId === 0);
        const result = [];
        if (isDir) {
            result.push({ id: 0, name: '— Racine (espace de premier niveau)', depth: 0, isPromote: true });
        }
        const addChildren = (parentId, depth) => {
            allDirs.filter((d) => d.parentId === parentId).forEach((d) => {
                result.push({ id: d.id, name: d.name, depth });
                addChildren(d.id, depth + 1);
            });
        };
        rootDirs.forEach((space) => {
            result.push({ id: space.id, name: space.name, depth: 0, isSpaceHeader: true });
            addChildren(space.id, 1);
        });
        return result;
    }, [allDirs, isDir]);

    const parentRef = currentParentId || 0;

    return (
        <>
            <div className="flex items-center justify-between px-6 py-3 border-b border-stone-200 flex-shrink-0">
                <div className="flex items-center gap-4">
                    {visibleTabs.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => { setTab(t.id); setError(''); setSuccess(''); }}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                tab === t.id
                                    ? 'bg-stone-900 text-white'
                                    : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700'
                            }`}
                        >
                            <t.icon size={14} />
                            {t.label}
                        </button>
                    ))}
                </div>
                <button onClick={onClose} className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 transition-colors" aria-label="Fermer">
                    <X size={18} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
                {tab === 'rename' && (
                    <div className="space-y-4">
                        <div>
                            <label className="field-label mb-1.5">
                                Nom {isDir ? 'du répertoire' : 'de la page'} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value.slice(0, MAX_NAME))}
                                onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                                autoFocus
                                className="input"
                            />
                            <p className="text-xs text-stone-400 mt-1 text-right">{newName.length}/{MAX_NAME}</p>
                        </div>
                        {/* L'icône ne concerne plus que les PAGES : un répertoire
                            n'en porte plus. */}
                        {!isDir && (
                        <div>
                            {/* `span` et non `label` : le choix se fait par des
                                boutons, un `label for=` ne désignerait aucun champ. */}
                            <span className="field-label mb-1.5 block">Icône</span>
                            <div className="flex items-center gap-3 flex-wrap">
                                <PictoPicker
                                    value={newIcon}
                                    onChange={setNewIcon}
                                    label="l’icône"
                                />
                                <button
                                    type="button"
                                    onClick={() => setNewIcon('')}
                                    disabled={!newIcon}
                                    className="btn btn-ghost btn-sm inline-flex items-center gap-1.5 disabled:opacity-40"
                                >
                                    <Ban size={14} aria-hidden="true" /> Aucune icône
                                </button>
                            </div>
                        </div>
                        )}
                        <button
                            type="button"
                            onClick={handleRename}
                            disabled={saving || !newName.trim() || !hasRenameChanges}
                            className="btn btn-primary btn-block flex items-center justify-center gap-2"
                        >
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            Enregistrer
                        </button>
                    </div>
                )}

                {tab === 'move' && (
                    <div className="space-y-4">
                        <p className="text-sm text-stone-500">
                            Choisissez le nouvel emplacement {isDir ? 'parent pour ce répertoire' : 'pour cette page'}.
                        </p>
                        {loadingDirs ? (
                            <div className="flex items-center gap-2 py-6 justify-center text-stone-400">
                                <Loader2 size={16} className="animate-spin" /> Chargement…
                            </div>
                        ) : (
                            <div className="border border-stone-200 rounded-xl max-h-64 overflow-y-auto">
                                {dirOptions.map((d, idx) => {
                                    if (d.isSpaceHeader) {
                                        const isSelected = String(d.id) === String(targetParentId);
                                        const isCurrent = d.id === parentRef;
                                        return (
                                            <button
                                                key={`s-${d.id}`}
                                                type="button"
                                                onClick={() => setTargetParentId(String(d.id))}
                                                className={`w-full text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider flex items-center gap-2 border-b border-stone-100 ${
                                                    isSelected ? 'bg-blue-50 text-blue-700' : 'bg-stone-50 text-stone-500 hover:bg-stone-100'
                                                } ${idx > 0 && !dirOptions[idx - 1]?.isPromote ? 'border-t border-stone-200' : ''}`}
                                            >
                                                <span>📁 {d.name}</span>
                                                {isCurrent && <span className="text-3xs text-stone-400 ml-auto font-normal normal-case">(actuel)</span>}
                                                {isSelected && <Check size={12} className="text-blue-600 ml-auto" />}
                                            </button>
                                        );
                                    }
                                    const isSelected = String(d.id) === String(targetParentId);
                                    const isCurrent = d.id === parentRef;
                                    return (
                                        <button
                                            key={d.id}
                                            type="button"
                                            onClick={() => setTargetParentId(String(d.id))}
                                            className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors border-b border-stone-50 last:border-0 ${
                                                isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-stone-50 text-stone-700'
                                            } ${d.isPromote ? 'font-medium text-amber-700 bg-amber-50/50 hover:bg-amber-50' : ''}`}
                                            style={{ paddingLeft: `${(d.depth || 0) * 16 + 12}px` }}
                                        >
                                            {(d.depth || 0) > 0 && <ChevronRight size={12} className="text-stone-300" />}
                                            <span>{d.isPromote ? '🌐' : '📁'} {d.name}</span>
                                            {isCurrent && <span className="text-3xs text-stone-400 ml-auto">(actuel)</span>}
                                            {isSelected && <Check size={14} className="text-blue-600 ml-auto" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Move preview: access diff */}
                        {loadingPreview && (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 size={16} className="animate-spin text-stone-400" />
                                <span className="ml-2 text-sm text-stone-500">Analyse des droits…</span>
                            </div>
                        )}
                        {movePreviewData && !loadingPreview && (
                            <MovePreviewInline
                                preview={movePreviewData}
                                isDir={isDir}
                                preserveAccess={preserveAccess}
                                onTogglePreserve={setPreserveAccess}
                            />
                        )}

                        {/* Promotion form: when destination = Root */}
                        {targetParentId === '0' && isDir && (
                            <PromotionForm
                                preview={movePreviewData}
                                projectId={promoteProjectId}
                                onProjectChange={setPromoteProjectId}
                                projects={promoteProjects}
                                categoryIds={promoteCategoryIds}
                                onCategoryToggle={(catId) => {
                                    setPromoteCategoryIds((prev) =>
                                        prev.includes(catId) ? prev.filter((id) => id !== catId) : [...prev, catId],
                                    );
                                }}
                                spaceCategories={filtersCtx?.spaceCategories ?? []}
                            />
                        )}

                        <button
                            type="button"
                            onClick={handleMove}
                            disabled={saving || String(targetParentId) === String(parentRef) || loadingPreview}
                            className="btn btn-primary btn-block flex items-center justify-center gap-2"
                        >
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            {targetParentId === '0' ? 'Promouvoir en espace' : 'Déplacer'}
                        </button>
                    </div>
                )}

                {tab === 'access' && isDir && (
                    <div className="space-y-5">
                        {loadingAccess ? (
                            <div className="flex items-center gap-2 py-8 justify-center text-stone-400">
                                <Loader2 size={16} className="animate-spin" /> Chargement…
                            </div>
                        ) : (
                            <>
                                {!isRoot && (
                                    <div className="flex items-start gap-3 p-3 rounded-xl border border-stone-200 bg-stone-50">
                                        <button
                                            type="button"
                                            onClick={() => setOverrideAccess((v) => !v)}
                                            className={`mt-0.5 relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${overrideAccess ? 'bg-amber-500' : 'bg-stone-300'}`}
                                        >
                                            <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${overrideAccess ? 'translate-x-4' : 'translate-x-0'}`} />
                                        </button>
                                        <div>
                                            <p className="text-sm font-medium text-stone-800">Couper l'héritage</p>
                                            {overrideAccess && (
                                                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                                                    <AlertTriangle size={11} /> Seuls les grants locaux s'appliqueront.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                        <Users size={12} /> Effectifs ({effectiveGrants.length})
                                    </h4>
                                    {effectiveGrants.length === 0 ? (
                                        <p className="text-sm text-stone-400 italic">Aucun accès.</p>
                                    ) : (
                                        <div className="border border-stone-200 rounded-xl overflow-hidden">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="bg-stone-50 text-stone-500 text-2xs">
                                                        <th className="text-left px-3 py-1.5 font-medium">Sujet</th>
                                                        <th className="text-center px-1 py-1.5 font-medium w-10">Src</th>
                                                        {PERM_KEYS.map((k) => (
                                                            <th key={k} className="text-center px-1 py-1.5 font-medium w-12">{PERM_LABELS[k]}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-stone-100">
                                                    {effectiveGrants.map((g, i) => (
                                                        <tr key={i} className={g.isInherited ? 'text-stone-400 italic' : ''}>
                                                            <td className="px-3 py-1.5"><GrantLabel grant={g} /></td>
                                                            <td className="text-center px-1 py-1.5">
                                                                <span className={`text-3xs px-1 py-0.5 rounded font-medium ${g.isInherited ? 'bg-stone-100 text-stone-400' : 'bg-blue-50 text-blue-600'}`}>
                                                                    {g.isInherited ? 'H' : 'L'}
                                                                </span>
                                                            </td>
                                                            {PERM_KEYS.map((k) => (
                                                                <td key={k} className="text-center px-1 py-1.5"><PermIcon granted={g[k]} /></td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-widest flex items-center gap-1.5">
                                            <Shield size={12} /> Locaux ({localGrants.length})
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={() => { setShowAddForm(true); loadSubjects(); }}
                                            className="btn btn-ghost btn-sm inline-flex items-center gap-1"
                                        >
                                            <Plus size={11} /> Ajouter
                                        </button>
                                    </div>

                                    {localGrants.length > 0 && (
                                        <div className="border border-stone-200 rounded-xl overflow-hidden mb-3">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="bg-stone-50 text-stone-500 text-2xs">
                                                        <th className="text-left px-3 py-1.5 font-medium">Sujet</th>
                                                        {PERM_KEYS.map((k) => (
                                                            <th key={k} className="text-center px-1 py-1.5 font-medium w-12">{PERM_LABELS[k]}</th>
                                                        ))}
                                                        <th className="w-8" />
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-stone-100">
                                                    {localGrants.map((g, i) => (
                                                        <tr key={i}>
                                                            <td className="px-3 py-1.5"><GrantLabel grant={g} /></td>
                                                            {PERM_KEYS.map((k) => (
                                                                <td key={k} className="text-center px-1 py-1.5">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!g[k]}
                                                                        onChange={() => setLocalGrants((prev) => prev.map((gg, ii) => ii === i ? { ...gg, [k]: !gg[k] } : gg))}
                                                                        className="h-3 w-3 rounded border-stone-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                                    />
                                                                </td>
                                                            ))}
                                                            <td className="text-center px-1 py-1.5">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setLocalGrants((prev) => prev.filter((_, ii) => ii !== i))}
                                                                    className="p-0.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                                                                >
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {showAddForm && (
                                        <div className="border border-blue-200 rounded-xl p-3 bg-blue-50/30 space-y-3">
                                            {subjectsLoading ? (
                                                <div className="flex items-center gap-2 text-sm text-stone-400 py-2">
                                                    <Loader2 size={14} className="animate-spin" /> Chargement…
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex gap-3">
                                                        <div className="flex-1">
                                                            <label className="text-2xs font-medium text-stone-600 mb-1 block">Type</label>
                                                            <select
                                                                value={addType}
                                                                onChange={(e) => { setAddType(e.target.value); setAddTargetId(''); setAddRoleId(''); setAddWikiGroupId(''); }}
                                                                className="select"
                                                            >
                                                                <option value="user">Utilisateur</option>
                                                                {hasProject && <option value="project_role">Rôle projet</option>}
                                                                <option value="wiki_role">Groupe Wiki</option>
                                                            </select>
                                                        </div>
                                                        {addType === 'user' && (
                                                            <div className="flex-1">
                                                                <label className="text-2xs font-medium text-stone-600 mb-1 block">Utilisateur</label>
                                                                <select
                                                                    value={addTargetId}
                                                                    onChange={(e) => setAddTargetId(e.target.value)}
                                                                    className="select"
                                                                >
                                                                    <option value="">Sélectionner…</option>
                                                                    {(subjects?.users || []).map((u) => (
                                                                        <option key={u.id} value={u.id}>{u.name}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                        {addType === 'project_role' && (
                                                            <div className="flex-1">
                                                                <label className="text-2xs font-medium text-stone-600 mb-1 block">Rôle</label>
                                                                <select
                                                                    value={addRoleId}
                                                                    onChange={(e) => {
                                                                        setAddRoleId(e.target.value);
                                                                        if (subjects?.projectId) setAddTargetId(String(subjects.projectId));
                                                                    }}
                                                                    className="select"
                                                                >
                                                                    <option value="">Sélectionner…</option>
                                                                    {(subjects?.availableRoles || []).map((r) => (
                                                                        <option key={r.id} value={r.id}>{r.label}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                        {addType === 'wiki_role' && (
                                                            <>
                                                                <div className="flex-1">
                                                                    <label className="text-2xs font-medium text-stone-600 mb-1 block">Groupe wiki</label>
                                                                    <select
                                                                        value={addWikiGroupId}
                                                                        onChange={(e) => { setAddWikiGroupId(e.target.value); setAddRoleId(''); }}
                                                                        className="select"
                                                                    >
                                                                        <option value="">Sélectionner…</option>
                                                                        {(subjects?.wikiGroups || []).map((g) => (
                                                                            <option key={g.id} value={g.id}>{g.name}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <label className="text-2xs font-medium text-stone-600 mb-1 block">Rôle wiki</label>
                                                                    <select
                                                                        value={addRoleId}
                                                                        onChange={(e) => setAddRoleId(e.target.value)}
                                                                        className="select"
                                                                    >
                                                                        <option value="">Sélectionner…</option>
                                                                        {(subjects?.availableWikiRoles || []).map((r) => (
                                                                            <option key={r.id} value={r.id}>{r.label}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-2.5">
                                                        {PERM_KEYS.map((k) => (
                                                            <label key={k} className="inline-flex items-center gap-1 text-xs text-stone-700 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={addPerms[k]}
                                                                    onChange={() => setAddPerms((p) => ({ ...p, [k]: !p[k] }))}
                                                                    className="h-3 w-3 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                                                                />
                                                                {PERM_LABELS[k]}
                                                            </label>
                                                        ))}
                                                    </div>
                                                    <div className="flex justify-end gap-2">
                                                        <button type="button" onClick={() => setShowAddForm(false)} className="btn btn-secondary btn-sm">Annuler</button>
                                                        <button
                                                            type="button"
                                                            onClick={handleAddGrant}
                                                            disabled={addType === 'user' ? !addTargetId : (addType === 'wiki_role' ? (!addWikiGroupId || !addRoleId) : !addRoleId)}
                                                            className="btn btn-primary btn-sm"
                                                        >
                                                            Ajouter
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={handleSaveAccess}
                                    disabled={saving}
                                    className="btn btn-primary btn-block flex items-center justify-center gap-2"
                                >
                                    {saving && <Loader2 size={14} className="animate-spin" />}
                                    Enregistrer les accès
                                </button>
                            </>
                        )}
                    </div>
                )}

                {tab === 'access' && !isDir && currentParentId && (
                    <div className="space-y-4">
                        <div className="flex items-start gap-2.5 p-3 rounded-xl border border-blue-200 bg-blue-50/50 text-sm text-blue-800">
                            <Info size={16} className="flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium">Droits hérités du dossier parent</p>
                                <p className="text-xs text-blue-600 mt-0.5">
                                    Les permissions de cette page sont définies au niveau
                                    du répertoire qui la contient. Pour modifier les droits,
                                    accédez aux paramètres du répertoire parent.
                                </p>
                            </div>
                        </div>
                        <DocAccessPanel
                            directoryId={currentParentId}
                            isActive={true}
                            readOnly={true}
                        />
                    </div>
                )}

                {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mt-4">{error}</p>}
                {success && <p className="text-sm text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg mt-4">{success}</p>}
            </div>
        </>
    );
}

/* ════════════════════════════════════════════════════════════════
   DocDirectorySettings — ReactiveJSON plugin wrapper
   Reads props from YAML via evaluateTemplateValue, delegates to DirectorySettingsBody.
   ════════════════════════════════════════════════════════════════ */

export const DocDirectorySettings = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const isOpen = evaluateTemplateValue({ valueToEvaluate: props.isOpen, templateContext, globalDataContext });
    const directoryId = evaluateTemplateValue({ valueToEvaluate: props.directoryId, templateContext, globalDataContext });
    const currentName = evaluateTemplateValue({ valueToEvaluate: props.currentName, templateContext, globalDataContext }) || '';
    const currentIcon = evaluateTemplateValue({ valueToEvaluate: props.currentIcon, templateContext, globalDataContext }) || '';
    const currentParentId = evaluateTemplateValue({ valueToEvaluate: props.currentParentId, templateContext, globalDataContext }) || 0;
    const openPath = props.openPath || 'showDirSettings';

    const handleClose = useCallback(() => {
        globalDataContext.updateData(false, openPath);
    }, [globalDataContext, openPath]);

    const handleSaved = useCallback(() => {
        setTimeout(() => window.location.reload(), 600);
    }, []);

    if (!isOpen) return null;

    return (
        <ActionDependant {...props}>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={handleClose} />
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-stone-200 max-h-[85vh] flex flex-col">
                    <DirectorySettingsBody
                        itemId={directoryId}
                        itemType="directory"
                        currentName={currentName}
                        currentIcon={currentIcon}
                        currentParentId={currentParentId}
                        onClose={handleClose}
                        onSaved={handleSaved}
                    />
                </div>
            </div>
        </ActionDependant>
    );
};
